import { useTranslation } from "react-i18next";
import { IconHeartHandshake } from "@tabler/icons-react";
import { cn } from "../../lib/utils";

// The two sizes the chip ships in. `md` is the default attribution chip (room
// header, join sheet, explainer); `sm` is the discreet variant for dense
// surfaces where the chip is a secondary detail, not a headline — e.g. the
// JuisciGPT room-suggestion card, where it sits under the room name.
const CHIP_SIZES = {
  md: { container: "gap-1 rounded-sm px-2 py-1 text-footnote", icon: "size-4" },
  sm: {
    container: "gap-1 rounded px-1.5 py-0.5 text-caption",
    icon: "size-3",
  },
} as const satisfies Record<string, { container: string; icon: string }>;

export type RoomSupportedByChipSize = keyof typeof CHIP_SIZES;

/**
 * The partner attribution chip — "Supported by [Partner]".
 *
 * Wording is locked by the Rooms positioning doc: the partner *supports* the
 * space, it does not steer the content. Same phrasing in the room header, the
 * join sheet and the explainer sheet, so it reads as one signature.
 */
export function RoomSupportedByChip({
  organisationName,
  size,
}: {
  organisationName: string;
  size: RoomSupportedByChipSize;
}): React.JSX.Element {
  const { t } = useTranslation();
  const styles = CHIP_SIZES[size];

  return (
    <span
      className={cn(
        "inline-flex items-center bg-ds-blueberry-50 font-dm-sans font-medium text-ds-blueberry-600",
        styles.container
      )}
    >
      <IconHeartHandshake
        className={cn("shrink-0", styles.icon)}
        stroke={1.5}
      />
      {t("Supported by {{organisationName}}", { organisationName })}
    </span>
  );
}
